import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Stack,
} from 'component-library';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { IUCNRedListCategory, Kingdom } from '@/api/rest/resources/types/species';

const SPECIES_DISPLAY_LIMIT = 3;

export type ThreatnedSpeciesSummaryData = {
  species_name: string;
  count: number;
  labels: {
    class: string;
    kingdom: Kingdom;
    landscape_radius_km: number;
    iucn_redlist_category: IUCNRedListCategory;
  };
};

export const ThreatnedSpeciesSummary = ({ data }: { data: PlotReportFactElement[] }) => {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<SpeciesListByCategory | null>(null);

  const handleDialogOpenChange = (open: boolean) => {
    if (open === false) {
      setSelectedGroup(null);
    }
  };

  const listData = getListData({ data });

  if (listData.length === 0) {
    return (
      <span className='typography-body1 text-text-secondary'>
        {t('shared.ncaDetail.details.threatenedSpecies.stats.noData')}
      </span>
    );
  }

  return (
    <Stack spacing={6} data-testid='threatened-species-summary'>
      {listData.map((item, index) => (
        <Stack key={`${item.category}-${index}`} className='typography-body1 text-text-secondary'>
          <span className='typography-body1Semibold'>{t(`global.iucn.description.${item.category}`)}</span>
          <ul>
            {item.species.slice(0, SPECIES_DISPLAY_LIMIT).map((species, idx) => (
              <li className='list-disc ml-5' key={`${item.category}-${species.name}-${idx}`}>
                {species.name} ({species.count})
              </li>
            ))}
            {item.species.length > SPECIES_DISPLAY_LIMIT && (
              <li className='list-disc ml-5'>
                <Button
                  variant='text'
                  className='p-0 underline hover:bg-transparent hover:text-primary-light'
                  onClick={() => setSelectedGroup(item)}
                >
                  {t('shared.ncaDetail.details.threatenedSpecies.stats.seeMore')}
                </Button>
              </li>
            )}
          </ul>
        </Stack>
      ))}
      {selectedGroup && <ThreatenedSpeciesDialog open onOpenChange={handleDialogOpenChange} data={selectedGroup} />}
    </Stack>
  );
};

type SpeciesListByCategory = {
  category: IUCNRedListCategory;
  species: {
    name: string;
    count: number;
    kingdom: Kingdom | undefined;
  }[];
};

const getListData = ({ data }: { data: PlotReportFactElement[] }) => {
  const dataByCategory = data.reduce<SpeciesListByCategory[]>((acc, curr) => {
    const category = curr.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.iucn_redlist_category)
      ?.value as IUCNRedListCategory | undefined;

    const speciesName = curr.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.species)?.value as
      | string
      | undefined;

    if (!category || !speciesName) {
      return acc;
    }

    const categoryInAcc = acc.find((c) => c.category === category);

    const kingdom = curr.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.kingdom)?.value as
      | Kingdom
      | undefined;

    const speciesEntry = {
      name: speciesName,
      count: curr.value as number,
      kingdom,
    };

    if (categoryInAcc) {
      categoryInAcc.species.push(speciesEntry);
    } else {
      acc.push({
        category,
        species: [speciesEntry],
      });
    }

    return acc;
  }, []);

  return dataByCategory.map((category) => ({
    ...category,
    species: category.species.sort((a, b) => b.count - a.count),
  }));
};

const ThreatenedSpeciesDialog: FC<DialogProps & { data: SpeciesListByCategory }> = ({ data, ...props }) => {
  const { t } = useTranslation();

  const categoryLabel = t(`global.iucn.description.${data.category}`);

  return (
    <Dialog {...props}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('shared.ncaDetail.details.threatenedSpecies.stats.dialog.title', {
              categoryLabel,
            })}
          </DialogTitle>
          <DialogBody className='max-h-[60vh] overflow-y-auto'>
            <ul>
              {data.species.map((species) => (
                <li className='list-disc ml-5 my-2' key={species.name}>
                  {species.name} ({species.count})
                </li>
              ))}
            </ul>
          </DialogBody>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
